import React, { useState } from 'react';
import axios from '../../api/axios';
import { FiMail, FiUserCheck, FiEdit, FiTrash2, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';

const BulkOperations = ({ selectedLeads, onComplete, onCancel }) => {
  const [operation, setOperation] = useState('');
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    ownerId: '',
    subject: '',
    message: '',
    assignStrategy: 'round-robin'
  });
  const [processing, setProcessing] = useState(false);

  const handleBulkUpdate = async () => {
    if (!formData.status && !formData.priority && !formData.ownerId) {
      toast.error('Select at least one field to update');
      return;
    }

    setProcessing(true);
    try {
      const updates = {};
      if (formData.status) updates.status = formData.status;
      if (formData.priority) updates.priority = formData.priority;
      if (formData.ownerId) updates.ownerId = formData.ownerId;

      await axios.put('/api/crm/leads/bulk', {
        leadIds: selectedLeads,
        updates
      });

      toast.success(`Updated ${selectedLeads.length} leads`);
      onComplete();
    } catch (error) {
      toast.error('Bulk update failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!formData.subject || !formData.message) {
      toast.error('Subject and message are required');
      return;
    }

    setProcessing(true);
    try {
      const { data } = await axios.post('/api/crm/leads/bulk-email', {
        leadIds: selectedLeads,
        subject: formData.subject,
        message: formData.message
      });

      toast.success(`Sent ${data.sent} emails`);
      onComplete();
    } catch (error) {
      toast.error('Bulk email failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleAutoAssign = async () => {
    setProcessing(true);
    try {
      await axios.post('/api/crm/leads/auto-assign', {
        leadIds: selectedLeads,
        strategy: formData.assignStrategy
      });

      toast.success(`Assigned ${selectedLeads.length} leads`);
      onComplete();
    } catch (error) {
      toast.error('Auto-assign failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedLeads.length} leads? This cannot be undone.`)) {
      return;
    }

    setProcessing(true);
    try {
      await axios.delete('/api/crm/leads/bulk', {
        data: { leadIds: selectedLeads }
      });

      toast.success(`Deleted ${selectedLeads.length} leads`);
      onComplete();
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    switch (operation) {
      case 'update':
        handleBulkUpdate();
        break;
      case 'email':
        handleBulkEmail();
        break;
      case 'assign':
        handleAutoAssign();
        break;
      case 'delete':
        handleBulkDelete();
        break;
      default:
        toast.error('Select an operation');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex justify-between items-center rounded-t-lg">
          <h2 className="text-xl font-semibold">
            Bulk Operations ({selectedLeads.length} leads selected)
          </h2>
          <button onClick={onCancel} className="hover:bg-blue-700 p-1 rounded">
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Operation
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setOperation('update')}
                className={`p-4 border-2 rounded-lg flex items-center gap-3 hover:bg-gray-50 ${
                  operation === 'update' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                }`}
              >
                <FiEdit size={20} />
                <span className="font-medium">Update Fields</span>
              </button>

              <button
                type="button"
                onClick={() => setOperation('email')}
                className={`p-4 border-2 rounded-lg flex items-center gap-3 hover:bg-gray-50 ${
                  operation === 'email' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                }`}
              >
                <FiMail size={20} />
                <span className="font-medium">Send Email</span>
              </button>

              <button
                type="button"
                onClick={() => setOperation('assign')}
                className={`p-4 border-2 rounded-lg flex items-center gap-3 hover:bg-gray-50 ${
                  operation === 'assign' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                }`}
              >
                <FiUserCheck size={20} />
                <span className="font-medium">Auto-Assign</span>
              </button>

              <button
                type="button"
                onClick={() => setOperation('delete')}
                className={`p-4 border-2 rounded-lg flex items-center gap-3 hover:bg-gray-50 ${
                  operation === 'delete' ? 'border-red-600 bg-red-50' : 'border-gray-300'
                }`}
              >
                <FiTrash2 size={20} />
                <span className="font-medium">Delete</span>
              </button>
            </div>
          </div>

          {/* Update Fields */}
          {operation === 'update' && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">-- No Change --</option>
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Converted">Converted</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">-- No Change --</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
          )}

          {/* Email Form */}
          {operation === 'email' && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Email subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 min-h-[150px]"
                  placeholder="Email message"
                  required
                />
              </div>
            </div>
          )}

          {/* Auto-Assign */}
          {operation === 'assign' && (
            <div className="space-y-3 border-t pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assignment Strategy
                </label>
                <select
                  value={formData.assignStrategy}
                  onChange={(e) => setFormData({ ...formData, assignStrategy: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="round-robin">Round Robin</option>
                  <option value="random">Random</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Leads will be automatically assigned to available sales reps
                </p>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {operation === 'delete' && (
            <div className="border-t pt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium">⚠️ Warning</p>
                <p className="text-red-700 text-sm mt-1">
                  This will permanently delete {selectedLeads.length} leads. This action cannot be undone.
                </p>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!operation || processing}
            className={`px-6 py-2 rounded-lg text-white disabled:opacity-50 ${
              operation === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {processing ? 'Processing...' : 'Execute'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkOperations;
